import { useState, useEffect } from 'react';
import { Link, useLocation, useHistory } from 'react-router-dom';
import { userRequest } from '../utils/requestMethods';
import { Header } from '../components';
import { useStateContext } from '../contexts/ContextProvider';
import { FiArrowLeft, FiArrowRight, FiTag, FiUsers, FiSearch } from 'react-icons/fi';
import { formatCurrency } from '../utils/currencyUtils';
import SkeletonTable from '../components/common/SkeletonTable';

const AddonPayments = () => {
  const history = useHistory();
  const location = useLocation();
  const classId = location.pathname.split('/')[2];
  const className = decodeURIComponent(location.pathname.split('/')[3] || '');
  const { school } = useStateContext();
  const [addons, setAddons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterText, setFilterText] = useState('');
  const schoolId = school?._id;

  useEffect(() => {
    if (schoolId && classId) fetchAddons();
  }, [classId, schoolId]);

  const fetchAddons = async () => {
    setLoading(true);
    try {
      const { data } = await userRequest.get(`class-addon/${schoolId}/${classId}`);
      setAddons((data.add_on || []).filter((a) => a?._id));
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="m-2 md:m-10 mt-24 p-4 md:p-10 bg-white rounded-3xl dark:bg-secondary-dark-bg min-h-screen">
      <button
        onClick={() => history.goBack()}
        className="inline-flex items-center gap-2 mb-4 text-sm font-medium text-gray-500 hover:text-indigo-600 transition-colors"
      >
        <FiArrowLeft className="w-4 h-4" />
        Back
      </button>

      <div className="mb-6">
        <Header category="Extra Fees" title={`${className} — Fees`} />
        <p className="text-sm text-gray-400 -mt-2">
          Select a fee to view which students have it and their payment status.
        </p>
      </div>

      {/* Toolbar */}
      {!loading && addons.length > 0 && (
        <div className="flex items-center justify-between mb-5">
          <p className="text-sm text-gray-500">
            {filterText
              ? `${addons.filter(a => a.add_on_name?.toLowerCase().includes(filterText.toLowerCase())).length} of ${addons.length} fees`
              : `${addons.length} fee${addons.length !== 1 ? 's' : ''}`}
          </p>
          <div className="relative">
            <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4 pointer-events-none" />
            <input
              type="text"
              value={filterText}
              onChange={e => setFilterText(e.target.value)}
              placeholder="Search fees..."
              className="pl-9 pr-4 py-2 text-sm rounded-full bg-gray-50 border border-gray-200 focus:outline-none focus:ring-2 focus:ring-indigo-300 focus:border-indigo-400 w-48 md:w-64"
            />
          </div>
        </div>
      )}

      {loading ? (
        <SkeletonTable rows={3} columns={3} />
      ) : addons.length === 0 ? (
        <div className="text-center py-16 text-gray-400 text-sm">No extra fees configured for this class.</div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
          {addons.filter(addon =>
            addon.add_on_name?.toLowerCase().includes(filterText.toLowerCase())
          ).map((addon) => (
            <Link key={addon._id} to={`/addon-payments/students/${addon._id}/${encodeURIComponent(addon.add_on_name)}`}>
              <div className="rounded-2xl border border-gray-200 bg-white p-5 flex flex-col gap-4 hover:shadow-md hover:border-indigo-200 transition-all cursor-pointer group">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2.5 rounded-xl bg-indigo-50">
                      <FiTag className="text-lg text-indigo-600" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-900 capitalize">{addon.add_on_name}</p>
                      <p className="text-xs text-gray-400 mt-0.5 capitalize">{className}</p>
                    </div>
                  </div>

                  {/* Price badge */}
                  <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-green-50 text-green-700 border border-green-200">
                    {formatCurrency(addon.add_on_price)}
                  </span>
                </div>

                <div className="flex items-center justify-between pt-1 border-t border-gray-100">
                  <div className="flex items-center gap-1.5 text-xs text-gray-400">
                    <FiUsers className="w-3.5 h-3.5" />
                    View student payments
                  </div>
                  <FiArrowRight className="text-gray-300 group-hover:text-indigo-500 transition-colors" />
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};

export default AddonPayments;
